import { PaginationCardMinimal } from "@admin-ui/components/application/pagination/pagination";
import { Table, TableCard } from "@admin-ui/components/primitives/application/table/table";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { toSortDescriptor } from "@admin-ui/utils/sort";
import type AdminLocationsIndex from "@travily/types/pages/AdminLocationsIndex";
import { MarkerPin01 } from "@untitledui/icons";
import type { SortDescriptor } from "react-aria-components";
import { TableBody } from "react-aria-components";

export type AdminLocationRow = AdminLocationsIndex["locations"][number];

export interface IndexViewProps {
  locations: AdminLocationRow[];
  pagy: AdminLocationsIndex["pagy"];
  sort: string | null;
  sortDir: string | null;
  withoutDestination: boolean;
  onPageChange: (page: number) => void;
  onSortChange: (descriptor: SortDescriptor) => void;
  onCreateDestination: (locationId: number) => void;
  onViewDestination: (destinationId: number) => void;
}

export function IndexView({
  locations,
  pagy,
  sort,
  sortDir,
  withoutDestination,
  onPageChange,
  onSortChange,
  onCreateDestination,
  onViewDestination,
}: IndexViewProps) {
  const sortDescriptor = toSortDescriptor(sort, sortDir);

  return (
    <>
      <TableCard.Root>
        <Table aria-label="Locations" sortDescriptor={sortDescriptor} onSortChange={onSortChange}>
          <Table.Header>
            <Table.Head id="name" label="Name" isRowHeader allowsSorting />
            <Table.Head id="country_code" label="Country" />
            <Table.Head id="full_address" label="Address" />
            <Table.Head id="destination" label="Destination" />
            <Table.Head id="trips_count" label="Trips" allowsSorting />
            <Table.Head id="actions" label="" />
          </Table.Header>
          <TableBody items={locations} renderEmptyState={() => <EmptyState filtered={withoutDestination} />}>
            {(location) => (
              <Table.Row key={location.id} id={location.id}>
                <Table.Cell>
                  <span className="text-sm font-medium text-primary">{location.name}</span>
                </Table.Cell>
                <Table.Cell>
                  <Badge color="gray" size="sm">
                    {location.country_code}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <span className="max-w-xs truncate text-sm text-tertiary">{location.full_address ?? "—"}</span>
                </Table.Cell>
                <Table.Cell>
                  <DestinationCell
                    destinationId={location.destination_id}
                    destinationName={location.destination_name}
                    onViewDestination={onViewDestination}
                  />
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-tertiary">{location.trips_count}</span>
                </Table.Cell>
                <Table.Cell>
                  {!location.destination_id && (
                    <Dropdown.Root>
                      <Dropdown.DotsButton />
                      <Dropdown.Popover className="w-min">
                        <Dropdown.Menu onAction={() => onCreateDestination(location.id)}>
                          <Dropdown.Item id="create-destination" icon={MarkerPin01} label="Create destination" />
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown.Root>
                  )}
                </Table.Cell>
              </Table.Row>
            )}
          </TableBody>
        </Table>
      </TableCard.Root>

      <PaginationCardMinimal align="right" page={pagy.page} total={pagy.pages} onPageChange={onPageChange} />
    </>
  );
}

function DestinationCell({
  destinationId,
  destinationName,
  onViewDestination,
}: {
  destinationId: number | null;
  destinationName: string | null;
  onViewDestination: (id: number) => void;
}) {
  if (destinationId && destinationName) {
    return (
      <button
        type="button"
        className="cursor-pointer text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover"
        onClick={() => onViewDestination(destinationId)}
      >
        {destinationName}
      </button>
    );
  }

  return (
    <Badge color="gray" size="sm">
      None
    </Badge>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="px-6 py-10 text-center text-sm text-tertiary">
      {filtered ? "All locations belong to a destination" : "No locations yet"}
    </div>
  );
}
